/**
 * useReservationFilters - 예약 목록 필터 상태 관리 훅
 *
 * V2: staged → applied 패턴 (ConsultListPage와 동일)
 * - 필터 변경 = staging만 (조회 안 됨)
 * - executeSearch() = appliedFilters 업데이트 → 조회 트리거
 * - resetFilters() = 모든 필터 기본값 + 조회
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

export type DatePreset =
  | "futureToday"
  | "thisWeek"
  | "thisMonth"
  | "all"
  | "custom";

export type ReservationSortOrder =
  | "appointmentDate,asc"
  | "appointmentDate,desc"
  | "occurredAt,desc"
  | "occurredAt,asc";

export type DateFieldType = "appointmentDate" | "occurredAt";

export interface ReservationFilterState {
  dateRange: { from: Date; to: Date };
  datePreset: DatePreset;
  dateField: DateFieldType;
  statusFilters: string[];
  searchQuery: string;
  sortOrder: ReservationSortOrder;
}

const STORAGE_KEY = "reservation-filters";

const DEFAULT_DATE_FIELD: DateFieldType = "appointmentDate";
const DEFAULT_SORT_ORDER: ReservationSortOrder = "appointmentDate,asc";
const DEFAULT_PRESET: DatePreset = "futureToday";

export function getDateRangeForPreset(preset: DatePreset): {
  from: Date;
  to: Date;
} {
  const now = dayjs();
  switch (preset) {
    case "futureToday":
      return {
        from: now.startOf("day").toDate(),
        to: now.add(3, "month").endOf("day").toDate(),
      };
    case "thisWeek":
      return {
        from: now.startOf("day").toDate(),
        to: now.add(7, "day").endOf("day").toDate(),
      };
    case "thisMonth":
      return {
        from: now.startOf("day").toDate(),
        to: now.add(1, "month").endOf("day").toDate(),
      };
    case "all":
      return {
        from: dayjs("2020-01-01").startOf("day").toDate(),
        to: dayjs("2030-12-31").endOf("day").toDate(),
      };
    case "custom":
    default:
      return {
        from: now.startOf("day").toDate(),
        to: now.add(1, "month").endOf("day").toDate(),
      };
  }
}

function parseStatusParam(param: string | null): string[] {
  if (!param) return [];
  return param.split(",").filter(Boolean);
}

function getDefaultFilters(): ReservationFilterState {
  return {
    dateRange: getDateRangeForPreset(DEFAULT_PRESET),
    datePreset: DEFAULT_PRESET,
    dateField: DEFAULT_DATE_FIELD,
    statusFilters: [],
    searchQuery: "",
    sortOrder: DEFAULT_SORT_ORDER,
  };
}

export function useReservationFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params (for initial state restoration)
  const presetParam = searchParams.get("preset") as DatePreset | null;
  const statusParam = searchParams.get("status");
  const qParam = searchParams.get("q");
  const sortParam = searchParams.get("sort") as ReservationSortOrder | null;
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const dateFieldParam = searchParams.get("dateField") as DateFieldType | null;

  // --- STAGED filter states (UI-only, don't trigger queries) ---

  const [datePreset, setDatePreset] = useState<DatePreset>(() => {
    if (presetParam) return presetParam;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved).datePreset ?? DEFAULT_PRESET;
    } catch {
      /* ignore */
    }
    return DEFAULT_PRESET;
  });

  const [dateRange, setDateRange] = useState(() => {
    if (fromParam && toParam) {
      return {
        from: dayjs(fromParam).toDate(),
        to: dayjs(toParam).toDate(),
      };
    }
    return getDateRangeForPreset(datePreset);
  });

  const [statusFilters, setStatusFilters] = useState<string[]>(() =>
    parseStatusParam(statusParam),
  );

  const [searchQuery, setSearchQuery] = useState(qParam || "");

  const [dateField, setDateField] = useState<DateFieldType>(
    dateFieldParam || DEFAULT_DATE_FIELD,
  );

  const [sortOrder, setSortOrder] = useState<ReservationSortOrder>(
    sortParam || DEFAULT_SORT_ORDER,
  );

  // --- APPLIED filter state (only this triggers queries) ---

  const [appliedFilters, setAppliedFilters] = useState<ReservationFilterState>(
    () => ({
      dateRange,
      datePreset,
      dateField,
      statusFilters,
      searchQuery,
      sortOrder,
    }),
  );

  // Update date range when preset changes (staging only)
  useEffect(() => {
    if (datePreset !== "custom") {
      setDateRange(getDateRangeForPreset(datePreset));
    }
  }, [datePreset]);

  // Auto-sync sort order when dateField changes (staging only)
  useEffect(() => {
    if (dateField === "appointmentDate") {
      setSortOrder("appointmentDate,asc");
    } else {
      setSortOrder("occurredAt,desc");
    }
  }, [dateField]);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ datePreset }));
    } catch {
      /* ignore */
    }
  }, [datePreset]);

  // --- ACTIONS ---

  /** 검색 실행: staged filters → applied filters → 조회 트리거 */
  const executeSearch = useCallback(() => {
    const newApplied: ReservationFilterState = {
      dateRange,
      datePreset,
      dateField,
      statusFilters,
      searchQuery,
      sortOrder,
    };
    setAppliedFilters(newApplied);

    // Sync URL params
    const params = new URLSearchParams();
    if (datePreset !== DEFAULT_PRESET) params.set("preset", datePreset);
    if (datePreset === "custom") {
      params.set("from", dayjs(dateRange.from).format("YYYY-MM-DD"));
      params.set("to", dayjs(dateRange.to).format("YYYY-MM-DD"));
    }
    if (statusFilters.length > 0) params.set("status", statusFilters.join(","));
    if (searchQuery) params.set("q", searchQuery);
    if (sortOrder !== DEFAULT_SORT_ORDER) params.set("sort", sortOrder);
    if (dateField !== DEFAULT_DATE_FIELD) params.set("dateField", dateField);

    setSearchParams(params, { replace: true });
  }, [
    dateRange,
    datePreset,
    dateField,
    statusFilters,
    searchQuery,
    sortOrder,
    setSearchParams,
  ]);

  /** 초기화: 모든 필터 기본값으로 리셋 + 즉시 조회 */
  const resetFilters = useCallback(() => {
    const defaults = getDefaultFilters();

    // Reset staged states
    setDatePreset(defaults.datePreset);
    setDateRange(defaults.dateRange);
    setDateField(defaults.dateField);
    setStatusFilters(defaults.statusFilters);
    setSearchQuery(defaults.searchQuery);
    setSortOrder(defaults.sortOrder);

    // Apply immediately
    setAppliedFilters(defaults);

    // Clear URL params
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const applyPreset = useCallback((preset: DatePreset) => {
    setDatePreset(preset);
    if (preset !== "custom") {
      setDateRange(getDateRangeForPreset(preset));
    }
  }, []);

  /** KPI 카드 클릭 전용: staged + applied 동시 설정 (stale closure 방지) */
  const applyCardFilter = useCallback(
    (cardAction: {
      statusFilters?: string[];
      datePreset?: string;
      getDateRange?: () => { from: Date; to: Date };
    }) => {
      const newStatusFilters = cardAction.statusFilters ?? [];

      // 카드에 getDateRange가 있으면 custom 범위 사용, 없으면 preset
      let newPreset: DatePreset;
      let newRange: { from: Date; to: Date };

      if (cardAction.getDateRange) {
        newPreset = "custom";
        newRange = cardAction.getDateRange();
      } else {
        newPreset = (cardAction.datePreset as DatePreset) ?? DEFAULT_PRESET;
        newRange = getDateRangeForPreset(newPreset);
      }

      // Stage
      setStatusFilters(newStatusFilters);
      setDatePreset(newPreset);
      setDateRange(newRange);

      // Apply immediately
      setAppliedFilters((prev) => ({
        ...prev,
        statusFilters: newStatusFilters,
        datePreset: newPreset,
        dateRange: newRange,
      }));

      // URL sync
      const params = new URLSearchParams();
      if (newPreset === "custom") {
        params.set("from", dayjs(newRange.from).format("YYYY-MM-DD"));
        params.set("to", dayjs(newRange.to).format("YYYY-MM-DD"));
      } else if (newPreset !== DEFAULT_PRESET) {
        params.set("preset", newPreset);
      }
      if (newStatusFilters.length > 0)
        params.set("status", newStatusFilters.join(","));
      if (sortOrder !== DEFAULT_SORT_ORDER) params.set("sort", sortOrder);
      setSearchParams(params, { replace: true });
    },
    [sortOrder, setSearchParams],
  );

  const applyCustomRange = useCallback((from: Date, to: Date) => {
    setDatePreset("custom");
    setDateRange({ from, to });
  }, []);

  const toggleStatus = useCallback((status: string) => {
    setStatusFilters((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  }, []);

  const clearStatusFilters = useCallback(() => {
    setStatusFilters([]);
    // 즉시 적용: "전체" 클릭 시 상태 필터 해제 + 조회
    setAppliedFilters((prev) => ({ ...prev, statusFilters: [] }));
    // URL에서 status 파라미터 제거
    const params = new URLSearchParams(searchParams);
    params.delete("status");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const setStatusFiltersDirect = useCallback(
    (statuses: string[]) => {
      setStatusFilters(statuses);
      // 즉시 적용: staged와 applied를 동시에 업데이트
      setAppliedFilters((prev) => ({ ...prev, statusFilters: statuses }));
      // URL 동기화
      const params = new URLSearchParams(searchParams);
      if (statuses.length > 0) params.set("status", statuses.join(","));
      else params.delete("status");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // staged filters (for UI display)
  const filters: ReservationFilterState = useMemo(
    () => ({
      dateRange,
      datePreset,
      dateField,
      statusFilters,
      searchQuery,
      sortOrder,
    }),
    [dateRange, datePreset, dateField, statusFilters, searchQuery, sortOrder],
  );

  return {
    filters, // staged (UI display)
    appliedFilters, // applied (query trigger)
    applyPreset,
    applyCardFilter,
    applyCustomRange,
    setSearchQuery,
    setSortOrder,
    setDateField,
    toggleStatus,
    clearStatusFilters,
    setStatusFiltersDirect,
    executeSearch,
    resetFilters,
  };
}
